import React, { useState, useEffect } from 'react';
import { Calculator, Settings, Edit3, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const FABRIC_WIDTH = 150; // cm

const FABRIC_TYPES = [
  { value: 'lana', label: 'Lana', factor: 1.0 },
  { value: 'lino', label: 'Lino', factor: 1.05 },
  { value: 'mezcla', label: 'Mezcla', factor: 1.0 },
  { value: 'algodon', label: 'Algodón', factor: 0.95 }
];

const CONSTRUCTION_TYPES = [
  { value: 'slim', label: 'Slim', factor: 0.95 },
  { value: 'regular', label: 'Regular', factor: 1.0 },
  { value: 'clasico', label: 'Clásico', factor: 1.1 }
];

export const FabricCalculator = ({ measurements, initialCalculation, onSave }) => {
  const [selectedGarments, setSelectedGarments] = useState({
    blazer: false,
    pantalon: false,
    chaleco: false,
    camisa: false,
    traje_completo: false
  });

  const [fabricType, setFabricType] = useState('lana');
  const [constructionType, setConstructionType] = useState('regular');
  const [calculatedResults, setCalculatedResults] = useState({});
  const [materials, setMaterials] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (initialCalculation) {
      setSelectedGarments(initialCalculation.garments_selected || selectedGarments);
      setFabricType(initialCalculation.fabric_type || 'lana');
      setConstructionType(initialCalculation.construction_type || 'regular');
      if (initialCalculation.calculated_results) {
        setCalculatedResults(initialCalculation.calculated_results);
      }
      if (initialCalculation.manual_adjustments && initialCalculation.manual_adjustments.materials) {
        setMaterials(initialCalculation.manual_adjustments.materials);
      }
    }
  }, [initialCalculation]);

  useEffect(() => {
    calculateFabric();
  }, [selectedGarments, fabricType, constructionType, measurements]);

  const parseFloat = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateBlazer = () => {
    const largo_blazer = parseFloat(measurements?.blazer?.largo_total || 0);
    const manga = parseFloat(measurements?.blazer?.manga || 0);
    const pecho = parseFloat(measurements?.blazer?.pecho || 0);
    
    if (largo_blazer === 0) return 0;
    
    const fabricFactor = FABRIC_TYPES.find(f => f.value === fabricType)?.factor || 1.0;
    const constructionFactor = CONSTRUCTION_TYPES.find(c => c.value === constructionType)?.factor || 1.0;
    
    let metraje = (largo_blazer + manga + 20) / 100;
    
    if (pecho > 110) metraje += 0.2;
    
    metraje = metraje * fabricFactor * constructionFactor;
    
    return Math.max(1.6, Math.min(2.5, metraje));
  };

  const calculatePantalon = () => {
    const largo = parseFloat(measurements?.pantalon?.largo_total || 0);
    const cadera = parseFloat(measurements?.pantalon?.base_cadera || 0);
    
    if (largo === 0) return 0;
    
    const fabricFactor = FABRIC_TYPES.find(f => f.value === fabricType)?.factor || 1.0;
    const constructionFactor = CONSTRUCTION_TYPES.find(c => c.value === constructionType)?.factor || 1.0;
    
    let metraje = (2 * largo + 20) / 100;
    
    if (cadera > 110) metraje += 0.15;
    
    metraje = metraje * fabricFactor * constructionFactor;
    
    return Math.max(1.2, Math.min(1.8, metraje));
  };

  const calculateChaleco = () => {
    const largo = parseFloat(measurements?.chaleco?.largo_total || 0);
    const pecho = parseFloat(measurements?.chaleco?.pecho || 0);
    
    if (largo === 0) return 0;
    
    const fabricFactor = FABRIC_TYPES.find(f => f.value === fabricType)?.factor || 1.0;
    
    let metraje = 0.8;
    
    if (pecho > 110) metraje = 1.0;
    else if (pecho < 90) metraje = 0.7;
    
    metraje = metraje * fabricFactor;
    
    return metraje;
  };

  const calculateCamisa = () => {
    const largo = parseFloat(measurements?.camisa?.largo_total || 0);
    const manga = parseFloat(measurements?.camisa?.manga || 0);
    const pecho = parseFloat(measurements?.camisa?.pecho || 0);
    
    if (largo === 0) return 0;
    
    let metraje = (largo + manga + 30) / 100;
    
    if (pecho > 110) metraje += 0.3;
    
    metraje = metraje * 1.2;
    
    return Math.max(1.8, Math.min(2.6, metraje));
  };

  const calculateFabric = () => {
    let telaPrincipal = 0;
    let forro = 0;
    let entretela = 0;
    const insumos = [];

    if (selectedGarments.traje_completo) {
      const blazerMetraje = calculateBlazer();
      const pantalonMetraje = calculatePantalon();
      telaPrincipal = blazerMetraje + pantalonMetraje;
      forro = blazerMetraje * 0.8 + pantalonMetraje * 0.3;
      entretela = 0.6;
      
      insumos.push(
        { material: 'Botones blazer', cantidad: 4, unidad: 'unidades' },
        { material: 'Botones manga', cantidad: 4, unidad: 'unidades' },
        { material: 'Botón pantalón', cantidad: 1, unidad: 'unidad' },
        { material: 'Cremallera pantalón', cantidad: 1, unidad: 'unidad' },
        { material: 'Gancho pantalón', cantidad: 1, unidad: 'unidad' },
        { material: 'Hilo', cantidad: 2, unidad: 'carretes' }
      );
    } else {
      if (selectedGarments.blazer) {
        const metraje = calculateBlazer();
        telaPrincipal += metraje;
        forro += metraje * 0.8;
        entretela += 0.4;
        insumos.push(
          { material: 'Botones blazer', cantidad: 4, unidad: 'unidades' },
          { material: 'Botones manga', cantidad: 4, unidad: 'unidades' },
          { material: 'Hilo', cantidad: 1, unidad: 'carrete' }
        );
      }

      if (selectedGarments.pantalon) {
        const metraje = calculatePantalon();
        telaPrincipal += metraje;
        forro += metraje * 0.3;
        entretela += 0.2;
        insumos.push(
          { material: 'Botón pantalón', cantidad: 1, unidad: 'unidad' },
          { material: 'Cremallera pantalón', cantidad: 1, unidad: 'unidad' },
          { material: 'Gancho pantalón', cantidad: 1, unidad: 'unidad' },
          { material: 'Hilo', cantidad: 1, unidad: 'carrete' }
        );
      }

      if (selectedGarments.chaleco) {
        const metraje = calculateChaleco();
        telaPrincipal += metraje;
        forro += metraje * 0.9;
        entretela += 0.3;
        insumos.push(
          { material: 'Botones chaleco', cantidad: 5, unidad: 'unidades' },
          { material: 'Hilo', cantidad: 1, unidad: 'carrete' }
        );
      }

      if (selectedGarments.camisa) {
        const metraje = calculateCamisa();
        telaPrincipal += metraje;
        entretela += 0.2;
        insumos.push(
          { material: 'Botones camisa', cantidad: 7, unidad: 'unidades' },
          { material: 'Entretela cuello', cantidad: 0.3, unidad: 'metros' },
          { material: 'Hilo', cantidad: 1, unidad: 'carrete' }
        );
      }
    }

    const results = {
      tela_principal: Number(telaPrincipal.toFixed(2)),
      forro: Number(forro.toFixed(2)),
      entretela: Number(entretela.toFixed(2)),
      insumos
    };

    setCalculatedResults(results);

    if (materials.length === 0 || !isEditing) {
      const materialsList = [
        { id: '1', material: 'Tela principal', cantidad: results.tela_principal, unidad: 'metros' },
        { id: '2', material: 'Forro', cantidad: results.forro, unidad: 'metros' },
        { id: '3', material: 'Entretela', cantidad: results.entretela, unidad: 'metros' },
        ...insumos.map((item, idx) => ({
          id: `insumo-${idx}`,
          material: item.material,
          cantidad: item.cantidad,
          unidad: item.unidad
        }))
      ];
      setMaterials(materialsList);
    }
  };

  const handleGarmentToggle = (garment) => {
    const newSelection = { ...selectedGarments };
    
    if (garment === 'traje_completo') {
      newSelection.traje_completo = !newSelection.traje_completo;
      if (newSelection.traje_completo) {
        newSelection.blazer = false;
        newSelection.pantalon = false;
        newSelection.chaleco = false;
        newSelection.camisa = false;
      }
    } else {
      newSelection[garment] = !newSelection[garment];
      if (newSelection[garment]) {
        newSelection.traje_completo = false;
      }
    }
    
    setSelectedGarments(newSelection);
  };

  const handleMaterialChange = (id, field, value) => {
    setMaterials(materials.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
    setIsEditing(true);
  };

  const handleSave = () => {
    const calculationData = {
      garments_selected: selectedGarments,
      fabric_type: fabricType,
      construction_type: constructionType,
      calculated_results: calculatedResults,
      manual_adjustments: {
        materials
      }
    };
    
    onSave(calculationData);
    toast.success('Cálculo guardado');
    setIsEditing(false);
  };

  const handleRecalculate = () => {
    setIsEditing(false);
    calculateFabric();
    toast.success('Recalculado automáticamente');
  };

  const hasSelectedGarments = Object.values(selectedGarments).some(v => v);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 p-4 rounded-none">
        <div className="flex items-start gap-2">
          <Calculator className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">
              Cálculo Automático Inteligente
            </p>
            <p className="text-xs text-blue-700">
              Este módulo calcula automáticamente el metraje e insumos basándose en las medidas registradas.
              Para uso interno del taller - No se muestra al cliente.
            </p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-sm uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Seleccionar Prendas a Calcular
        </h4>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { key: 'traje_completo', label: 'Traje Completo' },
            { key: 'blazer', label: 'Blazer' },
            { key: 'pantalon', label: 'Pantalón' },
            { key: 'chaleco', label: 'Chaleco' },
            { key: 'camisa', label: 'Camisa' }
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => handleGarmentToggle(key)}
              className={`p-4 border rounded-none text-sm transition-all ${
                selectedGarments[key]
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-black border-border hover:border-black'
              }`}
              data-testid={`garment-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
            Tipo de Tela
          </label>
          <select
            value={fabricType}
            onChange={(e) => setFabricType(e.target.value)}
            className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
            data-testid="fabric-type"
          >
            {FABRIC_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
            Tipo de Construcción
          </label>
          <select
            value={constructionType}
            onChange={(e) => setConstructionType(e.target.value)}
            className="w-full h-10 border border-input bg-white px-3 text-sm focus:outline-none focus:border-black"
            data-testid="construction-type"
          >
            {CONSTRUCTION_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {hasSelectedGarments && (
        <>
          <div className="bg-green-50 border border-green-200 p-6 rounded-none">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm uppercase tracking-wider text-green-900 font-medium">
                Resultados del Cálculo
              </h4>
              <button
                type="button"
                onClick={handleRecalculate}
                className="flex items-center gap-2 text-xs uppercase tracking-wider text-green-700 hover:text-green-900"
                data-testid="recalculate"
              >
                <RefreshCw className="w-4 h-4" />
                Recalcular
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-green-700 mb-1">Tela Principal</p>
                <p className="text-2xl font-serif font-medium text-green-900">
                  {calculatedResults.tela_principal || 0} m
                </p>
              </div>
              <div>
                <p className="text-xs text-green-700 mb-1">Forro</p>
                <p className="text-2xl font-serif font-medium text-green-900">
                  {calculatedResults.forro || 0} m
                </p>
              </div>
              <div>
                <p className="text-xs text-green-700 mb-1">Entretela</p>
                <p className="text-2xl font-serif font-medium text-green-900">
                  {calculatedResults.entretela || 0} m
                </p>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-3">
              * Basado en tela estándar de {FABRIC_WIDTH}cm de ancho
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Tabla de Materiales (Editable)
              </h4>
            </div>

            <div className="overflow-x-auto border border-border">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="border border-border px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Material
                    </th>
                    <th className="border border-border px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Cantidad
                    </th>
                    <th className="border border-border px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">
                      Unidad
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {materials.map((item) => (
                    <tr key={item.id} data-testid={`material-row-${item.id}`}>
                      <td className="border border-border px-4 py-2">
                        <input
                          type="text"
                          value={item.material}
                          onChange={(e) => handleMaterialChange(item.id, 'material', e.target.value)}
                          className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        />
                      </td>
                      <td className="border border-border px-4 py-2">
                        <input
                          type="number"
                          step="0.1"
                          value={item.cantidad}
                          onChange={(e) => handleMaterialChange(item.id, 'cantidad', parseFloat(e.target.value))}
                          className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        />
                      </td>
                      <td className="border border-border px-4 py-2">
                        <input
                          type="text"
                          value={item.unidad}
                          onChange={(e) => handleMaterialChange(item.id, 'unidad', e.target.value)}
                          className="w-full h-8 border border-input bg-white px-2 text-sm focus:outline-none focus:border-black"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSave}
            className="w-full bg-black text-white hover:bg-zinc-800 rounded-none h-12 uppercase tracking-widest text-xs font-medium transition-all"
            data-testid="save-fabric-calculation"
          >
            Guardar Cálculo de Metraje
          </button>
        </>
      )}

      {!hasSelectedGarments && (
        <div className="text-center py-12 bg-zinc-50 border border-border">
          <Calculator className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Selecciona al menos una prenda para calcular el metraje e insumos
          </p>
        </div>
      )}
    </div>
  );
};
